<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import { mediaHeader } from 'custom/helpers/channelTemplatePresets';
import TemplateSendMediaPicker from './TemplateSendMediaPicker.vue';

const props = defineProps({
  template: { type: Object, required: true },
  processedParams: { type: Object, required: true },
  mediaSource: { type: String, default: '' },
  uploadedMediaName: { type: String, default: '' },
  uploadingMedia: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['uploadMedia']);
const { t } = useI18n();

// `processedParams` is the parser's own reactive state, handed down so this
// component can lay the fields out in a readable order. Writing into it is the
// contract with the parser, not accidental prop mutation.
const params = computed(() => props.processedParams);

const header = computed(() => mediaHeader(props.template));
const hasMedia = computed(() => !!header.value);
const formatType = computed(() => {
  const format = header.value?.format || '';
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});
const isDocument = computed(() => header.value?.format === 'DOCUMENT');

const headerFields = computed(() =>
  Object.keys(params.value.header || {}).filter(
    key => !['media_url', 'media_type', 'media_name'].includes(key)
  )
);
const bodyFields = computed(() => Object.keys(params.value.body || {}));
const buttonFields = computed(() => params.value.buttons || []);
const hasVariables = computed(
  () =>
    headerFields.value.length ||
    bodyFields.value.length ||
    buttonFields.value.length
);

const mediaUrl = computed({
  get: () => params.value.header?.media_url || '',
  set: value => {
    params.value.header ??= {};
    params.value.header.media_url = value;
  },
});
const shouldWarnAboutMedia = computed(
  () => hasMedia.value && (!mediaUrl.value || props.mediaSource === 'meta')
);

const mediaName = computed({
  get: () => params.value.header?.media_name || '',
  set: value => {
    params.value.header ??= {};
    params.value.header.media_name = value;
  },
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <section v-if="hasMedia" class="rounded-xl border border-n-weak p-4">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('CHANNEL_TEMPLATE_PRESETS.MEDIA_SECTION') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{
          t('CHANNEL_TEMPLATE_PRESETS.MEDIA_SECTION_HELP', {
            format: formatType,
          })
        }}
      </p>

      <p
        v-if="shouldWarnAboutMedia"
        class="mt-3 rounded-lg bg-n-amber-3 p-3 text-sm text-n-amber-11"
      >
        <strong class="font-semibold">
          {{ t('CHANNEL_TEMPLATE_PRESETS.STATUS.not_configured') }}
        </strong>
        {{ t('CHANNEL_TEMPLATE_PRESETS.MEDIA_FOR_SEND_HELP') }}
      </p>

      <div class="mt-4 flex flex-col gap-4">
        <TemplateSendMediaPicker
          :template="template"
          :disabled="uploadingMedia"
          :filename="uploadedMediaName"
          :source="mediaSource"
          @select="file => emit('uploadMedia', file)"
        />

        <div>
          <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
            {{
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
                type: formatType,
              })
            }}
          </label>
          <Input
            v-model="mediaUrl"
            type="url"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
          />
        </div>

        <div v-if="isDocument">
          <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
            {{ t('CHANNEL_TEMPLATE_PRESETS.DOCUMENT_NAME_LABEL') }}
          </label>
          <Input
            v-model="mediaName"
            type="text"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
          />
        </div>
      </div>
    </section>

    <section v-if="hasVariables" class="rounded-xl border border-n-weak p-4">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLES_SECTION') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLES_SECTION_HELP') }}
      </p>

      <div class="mt-4 flex flex-col gap-5">
        <div v-if="headerFields.length" class="flex flex-col gap-2.5">
          <p
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLE_GROUPS.header') }}
          </p>
          <div v-for="key in headerFields" :key="`header-${key}`">
            <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
              {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLE_LABEL', { key }) }}
            </label>
            <Input
              v-model="params.header[key]"
              type="text"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                  variable: key,
                })
              "
            />
          </div>
        </div>

        <div v-if="bodyFields.length" class="flex flex-col gap-2.5">
          <p
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLE_GROUPS.body') }}
          </p>
          <div v-for="key in bodyFields" :key="`body-${key}`">
            <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
              {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLE_LABEL', { key }) }}
            </label>
            <Input
              v-model="params.body[key]"
              type="text"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                  variable: key,
                })
              "
            />
          </div>
        </div>

        <div v-if="buttonFields.length" class="flex flex-col gap-2.5">
          <p
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLE_GROUPS.buttons') }}
          </p>
          <div v-for="(button, index) in buttonFields" :key="`button-${index}`">
            <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
              {{
                t('WHATSAPP_TEMPLATES.PARSER.BUTTON_LABEL', {
                  index: index + 1,
                })
              }}
            </label>
            <Input
              v-model="button.parameter"
              type="text"
              :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
            />
          </div>
        </div>
      </div>
    </section>

    <p
      v-if="hasError"
      class="rounded-md bg-n-ruby-9/20 p-2.5 text-center text-sm text-n-ruby-11"
    >
      {{ t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
    </p>
  </div>
</template>
